import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

async function save(b, body, onSaved) {
  try {
    await api.put(`/pro/businesses/${b.id}`, body);
    toast.success("Modifications enregistrées");
    onSaved();
  } catch (err) {
    toast.error(errMsg(err));
  }
}

export function ContactEditor({ b, onSaved }) {
  const [f, setF] = useState({ phone: b.phone || "", email: b.email || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/pro/businesses/${b.id}/contact`, f);
      toast.success("Coordonnées enregistrées");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="contact-editor">
      <div>
        <p className="font-semibold">Téléphone et email <span className="ml-1 rounded-full bg-[#DCFCE7] px-2 py-0.5 text-xs text-[#14532D]">Gratuit</span></p>
        <p className="text-sm text-slate-500">Affichés sur votre fiche publique pour que les clients vous contactent.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label>Téléphone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="04 00 00 00 00" className="mt-1.5" data-testid="info-phone-input" /></div>
        <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" data-testid="info-email-input" /></div>
      </div>
      <Button type="submit" disabled={busy} className="bg-[#1E40AF] hover:bg-[#1D4ED8] hover:text-white" data-testid="contact-save-button">Enregistrer</Button>
    </form>
  );
}

export function InfoEditor({ b, onSaved }) {
  const [f, setF] = useState({ description: b.description || "", website: b.website || "", hours: b.hours || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(b, f, onSaved); }} className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="info-editor">
      <div><Label>Présentation</Label><Textarea rows={5} value={f.description} onChange={set("description")} className="mt-1.5" placeholder="Votre activité, votre zone d'intervention, vos garanties…" data-testid="info-description-input" /></div>
      <div><Label>Site web</Label><Input value={f.website} onChange={set("website")} className="mt-1.5" data-testid="info-website-input" /></div>
      <div><Label>Horaires</Label><Textarea rows={3} value={f.hours} onChange={set("hours")} className="mt-1.5" placeholder="Lun–Ven 8h–18h · Sam 9h–12h" data-testid="info-hours-input" /></div>
      <Button type="submit" className="bg-[#1E40AF] hover:bg-[#1D4ED8] hover:text-white" data-testid="info-save-button">Enregistrer</Button>
    </form>
  );
}

export function TagEditor({ b, field, title, placeholder, onSaved }) {
  const [tags, setTags] = useState(b[field] || []);
  const [val, setVal] = useState("");
  const add = (e) => {
    e.preventDefault();
    const v = val.trim();
    if (v && !tags.includes(v)) setTags([...tags, v]);
    setVal("");
  };
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid={`${field}-editor`}>
      <p className="font-semibold">{title}</p>
      <form onSubmit={add} className="flex gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} data-testid={`${field}-input`} />
        <Button type="submit" variant="outline" data-testid={`${field}-add-button`}>Ajouter</Button>
      </form>
      <div className="flex flex-wrap gap-2">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-sm">
            {t}<button onClick={() => setTags(tags.filter((x) => x !== t))} aria-label="Retirer" data-testid={`${field}-remove-${t}`}><X className="h-3.5 w-3.5 text-slate-500 hover:text-red-600" /></button>
          </span>
        ))}
      </div>
      <Button onClick={() => save(b, { [field]: tags }, onSaved)} className="bg-[#1E40AF] hover:bg-[#1D4ED8] hover:text-white" data-testid={`${field}-save-button`}>Enregistrer</Button>
    </div>
  );
}
